import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router";

import { ApiError, api, unwrap, type Schemas } from "@/api/client";
import { useAuth } from "@/app/auth";
import { Field, PasswordInput } from "@/components/common/Field";
import { TimezoneSelect } from "@/components/common/TimezoneSelect";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input, NativeSelect } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { languages, type Language } from "@/i18n";
import { errorText, fieldError } from "@/lib/errors";
import { formatShortDateTime } from "@/lib/format";

import { AuthShell, tokenFromHash } from "./AuthShell";

export function InvitePage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const { hash } = useLocation();
  const [token] = useState(() => tokenFromHash(hash));
  const invitation = useQuery({
    queryKey: ["invitation", token],
    enabled: token !== "",
    retry: false,
    queryFn: async () => unwrap(await api.POST("/api/v1/auth/invitation", { body: { token } })),
  });

  if (auth.status === "authenticated" && !invitation.data) return <Navigate to="/" replace />;

  if (!token || invitation.isError) {
    const expired =
      !token || (invitation.error instanceof ApiError && invitation.error.code === "invalid_token");
    return (
      <AuthShell title={t("invite.invalidTitle")}>
        <Alert variant={expired ? "warning" : "danger"}>
          {expired ? t("invite.invalidText") : errorText(t, invitation.error)}
        </Alert>
      </AuthShell>
    );
  }
  if (invitation.isPending) {
    return (
      <AuthShell title={<Skeleton className="mx-auto h-9 w-64" />}>
        <div className="flex flex-col gap-4">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      </AuthShell>
    );
  }
  return <AcceptForm token={token} invitation={invitation.data} />;
}

function AcceptForm({ token, invitation }: { token: string; invitation: Schemas["InvitationInfo"] }) {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const lang = i18n.resolvedLanguage ?? "ru";
  const [fullName, setFullName] = useState(invitation.full_name);
  const [timezone, setTimezone] = useState(invitation.timezone);
  const [locale, setLocale] = useState<Language>(
    (languages as readonly string[]).includes(lang) ? (lang as Language) : invitation.locale,
  );
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);
  const mismatch = confirm !== "" && confirm !== password;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const res = unwrap(
        await api.POST("/api/v1/auth/invitation/accept", {
          body: { token, full_name: fullName.trim(), password, timezone, locale },
        }),
      );
      window.history.replaceState(null, "", window.location.pathname);
      auth.signIn(res);
      void navigate("/", { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthShell
      wide
      title={t("invite.title", { company: invitation.company_name })}
      subtitle={t("invite.subtitle", { role: t(`roles.${invitation.role}`) })}
      footer={t("invite.expires", { date: formatShortDateTime(invitation.expires_at, lang) })}
    >
      <form className="flex flex-col gap-5" onSubmit={(e) => void submit(e)}>
        <Field label={t("invite.email")}>
          <Input value={invitation.email} readOnly disabled autoComplete="username" className="h-11" />
        </Field>
        <Field label={t("invite.fullName")} error={fieldError(t, error, "full_name")}>
          <Input
            required
            maxLength={120}
            autoComplete="name"
            className="h-11"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </Field>
        <div className="grid gap-5 sm:grid-cols-[1fr_auto]">
          <Field label={t("invite.timezone")}>
            <TimezoneSelect value={timezone} onChange={(e) => setTimezone(e.target.value)} className="h-11" />
          </Field>
          <Field label={t("invite.language")}>
            <NativeSelect
              value={locale}
              className="h-11"
              onChange={(e) => {
                const l = e.target.value as Language;
                setLocale(l);
                void i18n.changeLanguage(l);
              }}
            >
              {languages.map((l) => (
                <option key={l} value={l}>
                  {t(`languages.${l}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </div>
        <Field
          label={t("invite.password")}
          hint={t("password.hint")}
          error={fieldError(t, error, "password")}
        >
          <PasswordInput
            required
            autoFocus
            minLength={10}
            autoComplete="new-password"
            className="h-11"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label={t("password.confirm")} error={mismatch ? t("password.mismatch") : undefined}>
          <PasswordInput
            required
            autoComplete="new-password"
            className="h-11"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        <Card className="bg-surface-muted p-3 text-sm text-muted shadow-none">{t("invite.monitoring")}</Card>
        {error !== null && !fieldError(t, error, "password") && !fieldError(t, error, "full_name") && (
          <Alert variant="danger">{errorText(t, error)}</Alert>
        )}
        <Button
          type="submit"
          size="lg"
          disabled={pending || !fullName.trim() || password.length < 10 || confirm !== password}
        >
          {t("invite.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
